import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import "@/App.css";
import { Header } from "@/components/Header";
import { Hero } from "@/components/Hero";
import { About } from "@/components/About";
import { Services } from "@/components/Services";
import { HowItWorks } from "@/components/HowItWorks";
import { Testimonials } from "@/components/Testimonials";
import { Pricing } from "@/components/Pricing";
import { FAQ } from "@/components/FAQ";
import { Contact } from "@/components/Contact";
import { Partners } from "@/components/Partners";
import { Footer } from "@/components/Footer";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { SearchButton } from "@/components/SearchButton";
import { TravelSafeBanner } from "@/components/TravelSafeBanner";
import { Toaster } from "@/components/ui/sonner";
import SpellSuggest from "@/components/SpellSuggest";
import SilentErrorBoundary from "@/components/SilentErrorBoundary";

// Admin Pages
import { AdminLogin } from "@/pages/admin/AdminLogin";
import { AdminDashboard } from "@/pages/admin/AdminDashboard";
import { InvoicesList } from "@/pages/admin/InvoicesList";
import { CreateInvoice } from "@/pages/admin/CreateInvoice";
import { ViewInvoice } from "@/pages/admin/ViewInvoice";
import { QuotesList } from "@/pages/admin/QuotesList";
import { CreateQuote } from "@/pages/admin/CreateQuote";
import { ViewQuote } from "@/pages/admin/ViewQuote";
import { ReceiptsList } from "@/pages/admin/ReceiptsList";
import { CreateReceipt } from "@/pages/admin/CreateReceipt";
import { ViewReceipt } from "@/pages/admin/ViewReceipt";
import { ClientsList } from "@/pages/admin/ClientsList";
import { ViewClient } from "@/pages/admin/ViewClient";
import { CreateClient } from "@/pages/admin/CreateClient";
import { ClientEmailHistory } from "@/pages/admin/ClientEmailHistory";
import { EmailActivity } from "@/pages/admin/EmailActivity";
import { PetsList } from "@/pages/admin/PetsList";
import { PetFolder } from "@/pages/admin/PetFolder";
import { AnalyticsDashboard } from "@/pages/admin/AnalyticsDashboard";
import { PayCycles } from "@/pages/admin/PayCycles";
import { HealthCertificate } from "@/pages/admin/HealthCertificate";
import { HealthCertificatesList } from "@/pages/admin/HealthCertificatesList";
import { RefundsList } from "@/pages/admin/RefundsList";
import { CreatePet } from "@/pages/admin/CreatePet";
import { RelocationsList } from "@/pages/admin/RelocationsList";
import { CreateRelocation } from "@/pages/admin/CreateRelocation";
import { ViewRelocation } from "@/pages/admin/ViewRelocation";
import { DocumentsList } from "@/pages/admin/DocumentsList";
import { ReadinessReports } from "@/pages/admin/ReadinessReports";
import { ReportsHistory } from "@/pages/admin/ReportsHistory";
import { ManageContent } from "@/pages/admin/ManageContent";
import { Settings as AdminSettings } from "@/pages/admin/Settings";
import { TodoList } from "@/pages/admin/TodoList";
import PriceListManager from "@/pages/admin/PriceListManager";
import MicrochipVerifications from "@/pages/admin/MicrochipVerifications";
import RabiesVaccinations from "@/pages/admin/RabiesVaccinations";
import IntakeManager from "@/pages/admin/IntakeManager";
import EmailComposer from "@/pages/admin/EmailComposer";
import EmailDrafts from "@/pages/admin/EmailDrafts";
import VetClinics from "@/pages/admin/VetClinics";
import VetTrainingFlyerEditor from "@/pages/admin/VetTrainingFlyerEditor";
import AdminLibrary from "@/pages/admin/AdminLibrary";
import CaymanToUKGuideEditor from "@/pages/admin/CaymanToUKGuideEditor";
import BenefitsScopeFlyerEditor from "@/pages/admin/BenefitsScopeFlyerEditor";
import ServiceFlyerEditor from "@/pages/admin/ServiceFlyerEditor";
import CustomDictionary from "@/pages/admin/CustomDictionary";
import ConsultationTemplateSettings from "@/pages/admin/ConsultationTemplateSettings";
import ConsultationSummariesList from "@/pages/admin/ConsultationSummariesList";
import ConsultationSummaryEditor from "@/pages/admin/ConsultationSummaryEditor";
import MeetingSummariesList from "@/pages/admin/MeetingSummariesList";
import MeetingSummaryEditor from "@/pages/admin/MeetingSummaryEditor";
import MeetingSummaryPreview from "@/pages/admin/MeetingSummaryPreview";
import PublicCaymanToUKGuide from "@/pages/PublicCaymanToUKGuide";
import TitreTestExemptions from "@/pages/TitreTestExemptions";
import IntakeForm from "@/pages/IntakeForm";
import QuoteAcceptance from "@/pages/QuoteAcceptance";
import { RelocationTracker } from "@/pages/admin/RelocationTracker";
import { ImportChecklist } from "@/pages/ImportChecklist";
import { TestimonialForm } from "@/pages/TestimonialForm";

// Client Portal Pages
import { ClientLogin } from "@/pages/client/ClientLogin";
import { ClientDashboard } from "@/pages/client/ClientDashboard";

// Service Pages
import { TravelSafe } from "@/pages/TravelSafe";
import { BookTravelSafe } from "@/pages/BookTravelSafe";

// Mobile Admin PWA
import { AppShell } from "@/pages/app/AppShell";
import { AppLogin } from "@/pages/app/AppLogin";
import { AppHome } from "@/pages/app/AppHome";
import { AppRelocations } from "@/pages/app/AppRelocations";
import { AppInvoices } from "@/pages/app/AppInvoices";
import { AppReceipts } from "@/pages/app/AppReceipts";
import { AppMore } from "@/pages/app/AppMore";
import { AppScan } from "@/pages/app/AppScan";

// Main website component
const MainWebsite = () => {
  React.useEffect(() => {
    // Handle hash navigation after all content is loaded
    const handleHashNavigation = () => {
      const hash = window.location.hash;
      if (hash) {
        const id = hash.replace('#', '');
        const element = document.getElementById(id);
        if (element) {
          // Scroll immediately without smooth animation for reliability
          element.scrollIntoView({ behavior: 'auto', block: 'start' });
          // Then add a small offset for better visibility
          window.scrollBy(0, -80);
        }
      }
    };

    // Run immediately
    handleHashNavigation();
    
    // Also run after a delay to catch late-rendering components
    const timeoutId = setTimeout(handleHashNavigation, 1000);
    
    return () => clearTimeout(timeoutId);
  }, []);

  return (
    <div className="App">
      <Header />
      <Hero />
      
      <About />
      
      {/* Decorative Image - Beautiful Cayman Beach */}
      <div className="py-12 bg-muted/30">
        <div className="container mx-auto px-4 max-w-2xl">
          <img 
            src="/cayman-beach.png"
            alt="Pristine Cayman Islands beach with turquoise water and white sand"
            className="w-full h-auto rounded-2xl shadow-lg object-cover"
          />
        </div>
      </div>
      
      <Services />
      
      <TravelSafeBanner />
      <HowItWorks />
      <Testimonials />
      
      {/* Decorative Image 3 - After Testimonials */}
      <div className="py-12 bg-muted/20">
        <div className="container mx-auto px-4 max-w-2xl">
          <img 
            src="/family-with-dog.png"
            alt="Happy family with their beloved dog - trusted pet relocation"
            className="w-full h-auto rounded-2xl shadow-lg object-cover"
          />
        </div>
      </div>
      
      <Pricing />
      <FAQ />
      
      {/* Decorative Image - Cat Before Contact */}
      <div className="py-12 bg-muted/30">
        <div className="container mx-auto px-4 max-w-2xl">
          <img 
            src="/orange-cat.png"
            alt="Beautiful orange cat relaxing - trusted pet care"
            className="w-full h-auto rounded-2xl shadow-lg object-cover"
          />
        </div>
      </div>
      
      <Contact />
      <Partners />
      <Footer />
      <WhatsAppButton />
      <SearchButton />
      <Toaster />
    </div>
  );
};

function App() {
  // Global spell-check enabler — walks the DOM and sets spellcheck="true" on
  // every text input, textarea and contenteditable that hasn't opted out.
  // Runs once on mount and then via a MutationObserver so it also catches
  // fields rendered later (modals, dynamically-added rows, etc.).
  //
  // Also loads the admin's custom dictionary and, inside contenteditable
  // fields, wraps matching words in <span data-dict spellcheck="false">
  // so the browser stops red-underlining them. Browsers own spellcheck for
  // <input>/<textarea>, so wrapping there is impossible — but rich-text
  // fields (the Email Composer body is the biggest one) get real suppression.
  React.useEffect(() => {
    const NO_SPELL_TYPES = new Set(["email", "url", "password", "number", "tel", "search", "hidden", "file", "date", "time", "datetime-local"]);
    let dictWords = [];
    let dictRegex = null;

    const buildRegex = (words) => {
      const esc = words
        .filter((w) => w && w.trim())
        .map((w) => w.trim().replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
      if (esc.length === 0) return null;
      // Match whole words only (word boundaries), case-insensitive
      return new RegExp(`\\b(${esc.join("|")})\\b`, "gi");
    };

    const enableSpellcheck = (el) => {
      if (!(el instanceof HTMLElement)) return;
      if (el.getAttribute("spellcheck") === "false") return;
      if (el.tagName === "INPUT") {
        const t = (el.getAttribute("type") || "text").toLowerCase();
        if (NO_SPELL_TYPES.has(t)) return;
        if (!el.hasAttribute("spellcheck")) el.setAttribute("spellcheck", "true");
      } else if (el.tagName === "TEXTAREA") {
        if (!el.hasAttribute("spellcheck")) el.setAttribute("spellcheck", "true");
      } else if (el.isContentEditable) {
        if (!el.hasAttribute("spellcheck")) el.setAttribute("spellcheck", "true");
      }
    };

    // Walk text nodes inside a contenteditable and wrap dictionary hits.
    // Skips already-wrapped nodes and preserves the caret if it's inside.
    const wrapMatchesInEditable = (root) => {
      if (!dictRegex || !root) return;
      // Only work on truly editable containers
      if (!root.isContentEditable) return;
      // Preserve caret position by remembering the currently-focused editable
      const activeIsInside = root.contains(document.activeElement);
      if (activeIsInside) return; // don't wrap while the user is typing here

      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
        acceptNode: (node) => {
          const parent = node.parentElement;
          if (!parent) return NodeFilter.FILTER_REJECT;
          if (parent.dataset && parent.dataset.dict === "1") return NodeFilter.FILTER_REJECT;
          // Skip script/style
          if (["SCRIPT", "STYLE"].includes(parent.tagName)) return NodeFilter.FILTER_REJECT;
          return NodeFilter.FILTER_ACCEPT;
        },
      });
      const targets = [];
      let n;
      while ((n = walker.nextNode())) {
        if (dictRegex.test(n.nodeValue || "")) targets.push(n);
        dictRegex.lastIndex = 0;
      }
      for (const textNode of targets) {
        const text = textNode.nodeValue || "";
        const frag = document.createDocumentFragment();
        let lastIdx = 0;
        let m;
        dictRegex.lastIndex = 0;
        while ((m = dictRegex.exec(text)) !== null) {
          if (m.index > lastIdx) {
            frag.appendChild(document.createTextNode(text.slice(lastIdx, m.index)));
          }
          const span = document.createElement("span");
          span.dataset.dict = "1";
          span.setAttribute("spellcheck", "false");
          span.textContent = m[0];
          frag.appendChild(span);
          lastIdx = m.index + m[0].length;
        }
        if (lastIdx < text.length) frag.appendChild(document.createTextNode(text.slice(lastIdx)));
        textNode.parentNode && textNode.parentNode.replaceChild(frag, textNode);
      }
    };

    const scan = (root) => {
      if (!(root instanceof HTMLElement) && !(root instanceof Document)) return;
      const start = root instanceof Document ? root.documentElement : root;
      enableSpellcheck(start);
      if (start.querySelectorAll) {
        start.querySelectorAll("input, textarea, [contenteditable]").forEach((el) => {
          enableSpellcheck(el);
          if (el.isContentEditable) wrapMatchesInEditable(el);
        });
      }
    };

    // Debounced re-scan of contenteditables when their content changes —
    // catches paste + big edits without fighting the caret during typing.
    let editableScanTimer = null;
    const scheduleEditableRescan = () => {
      if (editableScanTimer) clearTimeout(editableScanTimer);
      editableScanTimer = setTimeout(() => {
        document.querySelectorAll("[contenteditable]").forEach((el) => {
          if (el.isContentEditable && !el.contains(document.activeElement)) {
            wrapMatchesInEditable(el);
          }
        });
      }, 600);
    };

    // Load the admin's dictionary (best-effort — silently no-ops if not signed in)
    const loadDictionary = () => {
      const pw = sessionStorage.getItem("admin_password");
      if (!pw) return;
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/settings/dictionary`, {
        headers: { Authorization: `Basic ${btoa(":" + pw)}` },
      })
        .then((r) => (r.ok ? r.json() : { words: [] }))
        .then((data) => {
          dictWords = data.words || [];
          dictRegex = buildRegex(dictWords);
          scan(document);
        })
        .catch(() => {});
    };
    loadDictionary();

    // Refresh dictionary when the settings page saves
    const onDictUpdate = (e) => {
      dictWords = e.detail || [];
      dictRegex = buildRegex(dictWords);
      scan(document);
    };
    window.addEventListener("pawsome:dictionary-updated", onDictUpdate);

    scan(document);
    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        m.addedNodes.forEach(scan);
        if (m.type === "characterData") scheduleEditableRescan();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true, characterData: true });

    return () => {
      observer.disconnect();
      window.removeEventListener("pawsome:dictionary-updated", onDictUpdate);
      if (editableScanTimer) clearTimeout(editableScanTimer);
    };
  }, []);

  return (
    <Router>
      <SilentErrorBoundary>
        <SpellSuggest />
      </SilentErrorBoundary>
      <Routes>
        {/* Main Website */}
        <Route path="/" element={<MainWebsite />} />
        
        {/* Public Pages */}
        <Route path="/import-checklist" element={<ImportChecklist />} />
        <Route path="/titre-test-exemptions" element={<TitreTestExemptions />} />
        <Route path="/testimonial/:token" element={<TestimonialForm />} />
        <Route path="/intake/:token" element={<IntakeForm />} />
        <Route path="/quote/:token" element={<QuoteAcceptance />} />
        
        {/* Service Pages */}
        <Route path="/travelsafe" element={<TravelSafe />} />
        <Route path="/book-travelsafe" element={<BookTravelSafe />} />
        
        {/* Client Portal Routes */}
        <Route path="/client/login" element={<ClientLogin />} />
        <Route path="/client/dashboard" element={<ClientDashboard />} />
        
        {/* Admin Routes */}
        <Route path="/admin" element={<Navigate to="/admin/login" replace />} />
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin/dashboard" element={<AdminDashboard />} />
        
        {/* Quotes */}
        <Route path="/admin/quotes" element={<QuotesList />} />
        <Route path="/admin/quotes/new" element={<CreateQuote />} />
        <Route path="/admin/quotes/:id" element={<ViewQuote />} />
        <Route path="/admin/quotes/:id/edit" element={<CreateQuote />} />
        
        {/* Invoices */}
        <Route path="/admin/invoices" element={<InvoicesList />} />
        <Route path="/admin/invoices/new" element={<CreateInvoice />} />
        <Route path="/admin/invoices/:id" element={<ViewInvoice />} />
        <Route path="/admin/invoices/:id/edit" element={<CreateInvoice />} />
        
        {/* Receipts */}
        <Route path="/admin/receipts" element={<ReceiptsList />} />
        <Route path="/admin/receipts/new" element={<CreateReceipt />} />
        <Route path="/admin/receipts/:id/edit" element={<CreateReceipt />} />
        <Route path="/admin/receipts/:id" element={<ViewReceipt />} />
        
        {/* Clients */}
        <Route path="/admin/clients" element={<ClientsList />} />
        <Route path="/admin/clients/new" element={<CreateClient />} />
        <Route path="/admin/clients/:clientEmail/emails" element={<ClientEmailHistory />} />
        <Route path="/admin/clients/:id" element={<ViewClient />} />
        <Route path="/admin/emails" element={<EmailActivity />} />
        <Route path="/admin/email-composer" element={<EmailComposer />} />
        <Route path="/admin/email-drafts" element={<EmailDrafts />} />
        <Route path="/admin/vet-clinics" element={<VetClinics />} />
        <Route path="/admin/vet-training-flyer/edit" element={<VetTrainingFlyerEditor />} />
        <Route path="/admin/library" element={<AdminLibrary />} />
        <Route path="/admin/guides/cayman-to-uk" element={<CaymanToUKGuideEditor />} />
        <Route path="/admin/flyers/benefits-scope" element={<BenefitsScopeFlyerEditor />} />
        <Route path="/admin/flyers/welcome" element={<ServiceFlyerEditor slug="welcome" />} />
        <Route path="/admin/flyers/concierge" element={<ServiceFlyerEditor slug="concierge" />} />
        <Route path="/admin/dictionary" element={<CustomDictionary />} />
        <Route path="/admin/consultation-template" element={<ConsultationTemplateSettings />} />
        <Route path="/admin/consultation-summaries" element={<ConsultationSummariesList />} />
        <Route path="/admin/meeting-summaries" element={<MeetingSummariesList />} />
        <Route path="/admin/meeting-summaries/new" element={<MeetingSummaryEditor />} />
        <Route path="/admin/meeting-summaries/:id/edit" element={<MeetingSummaryEditor />} />
        <Route path="/admin/meeting-summaries/:id/preview" element={<MeetingSummaryPreview />} />
        <Route path="/admin/consultation-summaries/new" element={<ConsultationSummaryEditor />} />
        <Route path="/admin/consultation-summaries/:id/edit" element={<ConsultationSummaryEditor />} />
        <Route path="/guides/cayman-to-uk" element={<PublicCaymanToUKGuide />} />
        <Route path="/admin/pets" element={<PetsList />} />
        <Route path="/admin/pets/new" element={<CreatePet />} />
        <Route path="/admin/pets/:id" element={<PetFolder />} />
        <Route path="/admin/analytics" element={<AnalyticsDashboard />} />
        <Route path="/admin/pay-cycles" element={<PayCycles />} />
        <Route path="/admin/health-certificates" element={<HealthCertificatesList />} />
        <Route path="/admin/health-certificates/new" element={<HealthCertificate />} />
        <Route path="/admin/health-certificates/:id/edit" element={<HealthCertificate />} />
        <Route path="/admin/refunds" element={<RefundsList />} />
        
        {/* Relocations */}
        <Route path="/admin/relocations" element={<RelocationsList />} />
        <Route path="/admin/relocations/new" element={<CreateRelocation />} />
        <Route path="/admin/relocations/:id" element={<ViewRelocation />} />
        <Route path="/admin/relocations/:id/edit" element={<CreateRelocation />} />
        
        {/* Documents */}
        <Route path="/admin/documents" element={<DocumentsList />} />
        
        {/* TravelSafe Readiness Reports */}
        <Route path="/admin/readiness-reports" element={<ReadinessReports />} />
        <Route path="/admin/reports-history" element={<ReportsHistory />} />
        
        {/* Content Management */}
        <Route path="/admin/content" element={<ManageContent />} />
        <Route path="/admin/price-list" element={<PriceListManager />} />
        <Route path="/admin/microchip-verifications" element={<MicrochipVerifications />} />
        <Route path="/admin/rabies-vaccinations" element={<RabiesVaccinations />} />
        <Route path="/admin/intake" element={<IntakeManager />} />
        <Route path="/admin/settings" element={<AdminSettings />} />
        <Route path="/admin/todos" element={<TodoList />} />
        
        {/* Pet Relocation Tracker */}
        <Route path="/admin/tracker" element={<RelocationTracker />} />

        {/* Mobile Admin PWA */}
        <Route path="/app" element={<Navigate to="/app/home" replace />} />
        <Route path="/app/login" element={<AppLogin />} />
        <Route path="/app" element={<AppShell />}>
          <Route path="home" element={<AppHome />} />
          <Route path="relocations" element={<AppRelocations />} />
          <Route path="invoices" element={<AppInvoices />} />
          <Route path="receipts" element={<AppReceipts />} />
          <Route path="scan" element={<AppScan />} />
          <Route path="more" element={<AppMore />} />
        </Route>
      </Routes>
    </Router>
  );
}

export default App;
